/**
 * A warning or a failure, as the final report shows it: a code and a sentence.
 *
 * The runner relays what migration-core persists on a tracking row, and the
 * schema keeps both as `unknown` on purpose. Today a warning is an `Issue`
 * (`{ code, blocking, message }`) and a failure a `MigrationFailure`
 * (`{ errorCode, errorMessage, details? }`), but a report someone shares is the
 * wrong place to discover a shape changed: anything else is stringified rather
 * than dropped, so the audit still says *something* was there.
 */

export interface ReportIssue {
    readonly code: string;
    readonly message: string;
    /** An `Issue`'s own flag; `null` when the shape carries none (a failure, a string). */
    readonly blocking: boolean | null;
}

/** The code a shape we cannot read is filed under. */
export const UNKNOWN_ISSUE_CODE = 'UNKNOWN';

export function stringify(value: unknown): string {
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value) ?? String(value);
    } catch {
        // A cycle, a BigInt: still better than nothing.
        return String(value);
    }
}

function textField(source: Record<string, unknown>, ...keys: string[]): string | undefined {
    for (const key of keys) {
        const value = source[key];
        if (typeof value === 'string' && value.length > 0) return value;
    }
    return undefined;
}

/** One item, of whatever shape: `Issue`, `MigrationFailure`, a string, or anything. */
export function normaliseIssue(value: unknown): ReportIssue {
    if (typeof value === 'string') {
        return { code: UNKNOWN_ISSUE_CODE, message: value, blocking: null };
    }
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const source = value as Record<string, unknown>;
        const code = textField(source, 'code', 'errorCode');
        const message = textField(source, 'message', 'errorMessage');
        if (code !== undefined || message !== undefined) {
            return {
                code: code ?? UNKNOWN_ISSUE_CODE,
                message: message ?? stringify(value),
                blocking: typeof source.blocking === 'boolean' ? source.blocking : null,
            };
        }
    }
    return { code: UNKNOWN_ISSUE_CODE, message: stringify(value), blocking: null };
}

/** A list of them; a lone item is a list of one, and nothing is an empty list. */
export function normaliseIssues(value: unknown): ReportIssue[] {
    if (value === null || value === undefined) return [];
    if (Array.isArray(value)) return value.map(normaliseIssue);
    return [normaliseIssue(value)];
}

/** A failure is present or not: `null` and `undefined` both mean "did not fail". */
export function normaliseFailure(value: unknown): ReportIssue | null {
    if (value === null || value === undefined) return null;
    return normaliseIssue(value);
}

/** `CODE: message`, the one-line form the CSV and the summaries use. */
export function issueLine(issue: Pick<ReportIssue, 'code' | 'message'>): string {
    return `${issue.code}: ${issue.message}`;
}
